import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import { goToUnitRosterYouthRank } from '@modules/roster-lite/duck/actions';
import { useGetYouthRanksQuery } from '@modules/youthProfile/api';
import YouthRanksCard from '@shared/components/YouthRanksCard';
import type { Program } from '@shared/constants';
import { ProgramId } from '@shared/constants';

interface YouthRanksProps {
  /** Scoutbook user id from the route */
  userId?: string;
  /** The program of the unit the roster was opened from. */
  activeProgram?: Program;
}

/**
 * The youth's ranks for the program of the unit they are being viewed in.
 *
 * Fetches its own data, so it shares nothing with the profile card beyond the
 * program it is told to show. The ranks endpoint answers with every program the
 * youth has ranks in; a roster viewer only cares about this one.
 *
 * "View More" opens the rank's requirements under the same `/unit-roster` tree,
 * so the viewer never leaves the roster's permission gate.
 */
export default function YouthRanks(props: YouthRanksProps) {
  const { userId, activeProgram } = props;

  const dispatch = useDispatch();
  const { orgGuid } = useSelector(routeParamsSel<{ orgGuid?: string }>);

  const { data, isFetching } = useGetYouthRanksQuery(
    // Same args the youthProfile module uses, so both read one cache entry
    { userId: userId as string, params: { status: 'all' } },
    { skip: !userId },
  );

  const programId = activeProgram ? ProgramId[activeProgram] : undefined;
  const program = data?.program?.find(p => Number(p.programId) === programId);

  const handleViewMore = (rankId: number) =>
    dispatch(goToUnitRosterYouthRank({ orgGuid, userId, rankId }));

  return (
    <YouthRanksCard
      program={program}
      // The program comes from the profile request, so until that lands there is
      // nothing to pick with, and an empty card would be a lie rather than a wait
      loading={isFetching || !activeProgram}
      // Without both ids there is no route to build, so no button either
      onViewMore={orgGuid && userId ? handleViewMore : undefined}
      // Same as the youth profile page: an unearned Bobcat is not shown
      hideUnearnedBobcat
    />
  );
}
